import { BannerVariant } from "@faire/web-api/indigofair/data/BannerVariant";
import { IBannerComponent } from "@faire/web-api/indigofair/data/IBannerComponent";
import React from "react";

import { SDUIOnClickHandler } from "@faire/retailer/components/SDUI/__internal__/SDUIOnClickHandler";
import {
  ComponentLoggingEventParams,
  SDUIComponentInstanceRendererProps,
  SDUIComponentRenderBehaviour,
} from "@faire/retailer/components/SDUI/__internal__/types";
import { SDUIBackgroundIcon } from "@faire/retailer/components/SDUI/ComponentRenderers/BackgroundIcon/SDUIBackgroundIcon";
import {
  SDUIConstrainedWidthAnnouncementBannerRenderer,
  SDUIDefaultAnnouncementBannerRenderer,
} from "@faire/retailer/components/SDUI/ComponentRenderers/Banner/__internal__/AnnouncementBannerRenderer/SDUIAnnouncementBannerRenderer";
import { SDUIImageBannerV2Renderer } from "@faire/retailer/components/SDUI/ComponentRenderers/Banner/__internal__/ImageBannerV2/SDUIImageBannerV2Renderer";
import {
  SDUIDefaultImageTextBannerRenderer,
  SDUIImageTextBannerRenderer,
} from "@faire/retailer/components/SDUI/ComponentRenderers/Banner/__internal__/ImageTextBanner/SDUIImageTextBannerRenderer";

import { SDUIVerticalModalTextImageBannerRenderer } from "./__internal__/VerticalModalTextImageBanner/SDUIVerticalModalTextImageBannerRenderer";

export const SDUIBannerRenderer: React.FC<
  SDUIComponentInstanceRendererProps
> = ({ component, loggingData, renderBehaviour }) => {
  const bannerComponent = component.banner_component;
  if (!bannerComponent || bannerComponent.style?.variant === undefined) {
    return null;
  }

  const SDUIBannerVariantRenderer =
    SDUIBannerVariantRendererMap[bannerComponent.style.variant];
  if (SDUIBannerVariantRenderer === undefined) {
    return null;
  }

  return (
    <SDUIOnClickHandler
      onClickActions={bannerComponent.on_click}
      loggingData={loggingData}
      componentStyle={{ position: "relative" }}
    >
      <SDUIBannerVariantRenderer
        bannerComponent={bannerComponent}
        loggingData={loggingData}
        renderBehaviour={renderBehaviour}
      />
      <SDUIBackgroundIcon
        backgroundIcon={bannerComponent.background_icon}
        responsiveBackgroundIcon={bannerComponent.responsive_background_icon}
      />
    </SDUIOnClickHandler>
  );
};

const SDUIBannerVariantRendererMap: Partial<
  Record<
    BannerVariant,
    | React.FC<{
        bannerComponent: IBannerComponent;
        loggingData: ComponentLoggingEventParams;
        renderBehaviour?: SDUIComponentRenderBehaviour;
      }>
    | undefined
  >
> = {
  [BannerVariant.TEXT_IMAGE_BANNER]: SDUIImageTextBannerRenderer,
  [BannerVariant.DEFAULT_IMAGE_TEXT_BANNER]: SDUIDefaultImageTextBannerRenderer,
  [BannerVariant.IMAGE_BANNER_V2]: SDUIImageBannerV2Renderer,
  [BannerVariant.IMAGE_BANNER]: SDUIImageBannerV2Renderer,
  [BannerVariant.ANNOUNCEMENT_BANNER]: SDUIDefaultAnnouncementBannerRenderer,
  [BannerVariant.ANNOUNCEMENT_BANNER_CONTENT_WIDTH_CONSTRAINED]:
    SDUIConstrainedWidthAnnouncementBannerRenderer,
  [BannerVariant.VERTICAL_MODAL_TEXT_IMAGE_BANNER]:
    SDUIVerticalModalTextImageBannerRenderer,
};
